
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { DeleteModal, Loading } from "../../components";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { Search, Eye, Pencil, Trash2 } from "lucide-react";
import OrderDetailsModal from "./OrderDetailsModal";
import EditOrderSidebar from "./EditOrderSidebar";

/* ---------- helpers ---------- */
function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const STATUS_UI = {
  pending: { label: "Nouvelle", cls: "bg-yellow-100 text-yellow-800" },

  confirmed: { label: "Confirmée", cls: "bg-green-100 text-green-800" },
  preparing: { label: "En préparation", cls: "bg-blue-100 text-blue-800" },
  shipped: { label: "Remise au transporteur", cls: "bg-indigo-100 text-indigo-800" },
  delivered: { label: "Livrée au client", cls: "bg-green-100 text-green-800" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-800" },
  refused: { label: "Refusée", cls: "bg-red-100 text-red-800" },
  no_answer: { label: "Pas de réponse", cls: "bg-gray-100 text-gray-800" },
};
function formatDateFR(dateString) {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Orders() {
  const { checkAccess } = useAccessChecker();

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebouncedValue(searchValue, 350);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [editSidebarOpen, setEditSidebarOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // prevent race conditions
  const reqKeyRef = useRef(0);

  const canEdit = checkAccess("orders", "update") || checkAccess("orders", "edit");
  const canDelete = checkAccess("orders", "delete");

  const fetchOrders = useCallback(
    async (page = 1) => {
      const key = ++reqKeyRef.current;

      try {
        setLoading(true);

        const payload = {
          search: debouncedSearch?.trim() || "",
        };

        const res = await axiosInstance.post(
          `/orders/search?page=${page}&per_page=10`,
          payload
        );

        if (key !== reqKeyRef.current) return;

        const r = res?.data;
        const rows = Array.isArray(r?.data) ? r.data : [];

        setOrders(rows);
        setCurrentPage(Number(r?.current_page || page));
        setTotalPages(Number(r?.last_page || 1));
      } catch (error) {
        if (key !== reqKeyRef.current) return;
        toast.error(error?.response?.data?.message || "Erreur lors du chargement des commandes");
      } finally {
        if (key === reqKeyRef.current) setLoading(false);
      }
    },
    [debouncedSearch]
  );

  // initial + when search changes
  useEffect(() => {
    fetchOrders(1);
  }, [fetchOrders]);

  const refresh = () => fetchOrders(currentPage);

  const openDetails = (order) => {
    setSelectedOrder(order);
    setDetailsModalOpen(true);
  };

  const openEdit = (order) => {
    setSelectedOrder(order);
    setEditSidebarOpen(true);
  };

  const openDelete = (order) => {
    setDeleteId(order.id);
    setDeleteModal(true);
  };

  const handleOrderUpdated = (updatedOrder) => {
    // optimistic UI update + refresh from server
    setOrders((prev) =>
      prev.map((o) => (o.id === updatedOrder?.id ? updatedOrder : o))
    );
    refresh();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col w-full h-full px-6 py-5 bg-white"
    >
      {loading && <Loading />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full mb-6 gap-4">
        <div>
          <h1 className="text-xl text-start font-semibold text-neutral-800">
            Commandes
          </h1>
          <p className="text-sm text-start text-neutral-600">
            Gérer et suivre les commandes clients
          </p>
        </div>

        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
          <input
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue}
            type="text"
            placeholder="Rechercher une commande..."
            className="pl-10 pr-4 py-2 w-full h-10 text-sm border border-neutral-200 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-200"
          />
        </div>
      </div>

      {/* Table */}
      <div className="flex flex-col">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden border border-neutral-200 md:rounded-lg">
              <table className="min-w-full divide-y divide-neutral-200">
                <thead className="bg-neutral-50 h-14">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      N° Commande
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="bg-white divide-y divide-neutral-200">
                  {orders.map((order) => {
                    const ui =
                      STATUS_UI[order?.status] || {
                        label: "Inconnu",
                        cls: "bg-gray-100 text-gray-800",
                      };

                    return (
                      <tr key={order.id} className="hover:bg-neutral-50 text-start">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-neutral-700">
                          {order.order_number || `#${order.id}`}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                          {`${order.first_name || ""} ${order.last_name || ""}`.trim() || "-"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-500">
                          {formatDateFR(order.created_at)}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${ui.cls}`}
                          >
                            {ui.label}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex justify-start items-center gap-3">
                            <button
                              onClick={() => openDetails(order)}
                              className="flex justify-center p-1 hover:scale-110 transition-all duration-200 items-center rounded-md hover:bg-neutral-200 text-neutral-600"
                              aria-label="Voir"
                              type="button"
                            >
                              <Eye size={16} />
                            </button>

                            {canEdit && (
                              <button
                                onClick={() => openEdit(order)}
                                className="flex justify-center p-1 hover:scale-110 transition-all duration-200 items-center rounded-md hover:bg-neutral-200 text-neutral-600"
                                aria-label="Modifier"
                                type="button"
                              >
                                <Pencil size={16} />
                              </button>
                            )}

                            {canDelete && (
                              <button
                                onClick={() => openDelete(order)}
                                className="flex justify-center p-1 hover:scale-110 transition-all duration-200 items-center rounded-md hover:bg-neutral-200 text-neutral-600"
                                aria-label="Supprimer"
                                type="button"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {!loading && orders.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-6 py-10 text-center text-sm text-neutral-500"
                      >
                        Aucune commande trouvée.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <nav className="relative z-0 inline-flex rounded-md -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => fetchOrders(currentPage - 1)}
                    disabled={loading || currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50"
                    type="button"
                  >
                    <span className="sr-only">Précédent</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={() => fetchOrders(currentPage + 1)}
                    disabled={loading || currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-neutral-300 bg-white text-sm font-medium text-neutral-500 hover:bg-neutral-50 disabled:opacity-50"
                    type="button"
                  >
                    <span className="sr-only">Suivant</span>
                    <svg
                      className="h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="orders"
        // if your DeleteModal needs refresh:
        setDone={() => refresh()}
        setLoding={setLoading}
        done={false}
      />

      <OrderDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => setDetailsModalOpen(false)}
        order={selectedOrder}
      />

      <EditOrderSidebar
        isOpen={editSidebarOpen}
        onClose={() => setEditSidebarOpen(false)}
        order={selectedOrder}
        onOrderUpdated={handleOrderUpdated}
      />
    </motion.div>
  );
}